import React, { useState, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

// MUI
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { useTheme, useMediaQuery } from "@mui/material";
import ClickableMobileStepper from '@/ui/PublicPage/ClickableMobileStepper'

// Custom Components
import DynamicDialog from "@/ui/Dialog/DynamicDialog";

// Types
import { IDialogProps } from "@/@types/ui/DialogContext";
import { ParsedRouteWarning } from "@/@types/services/mapService";

const PublicWarningDialog: FunctionComponent<IDialogProps> = (props) => {
  const { t } = useTranslation();

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));

  const [activeStep, setActiveStep] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  const minSwipeDistance = 50 

  const warnings = props.warnings as ParsedRouteWarning[];

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null) // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX)
  }
  
  const onTouchMove = (e: React.TouchEvent) => setTouchEnd(e.targetTouches[0].clientX)
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance
    
    if (isRightSwipe) {
      setActiveStep(prevState => Math.max(0,prevState-1));
    } else if (isLeftSwipe) {
      setActiveStep(prevState => Math.min(warnings.length-1,prevState+1));
    }
  }

  const { onClose } = props;

  const handleStepChange = (step: number) => {
    setActiveStep(step);
  }

  const handleClose = () => {
    onClose();
  };

  const isMSPWarning = warnings[activeStep].warning_type === "MSP";

  const activeColor = isMSPWarning ? "error" : "primary";

  const iconSrc = isMSPWarning ? "/images/icons/info_2.svg" : "/images/icons/info_1.svg";

  return (
    <DynamicDialog open maxWidth="custom" onClose={handleClose} disableFullscreen>
      <div style={{width:"100%",height:"100%"}} className="welcome-dialog-wrapper">
        <DialogContent sx={{ px: smUp ? "15px" : "5px", py:0, m: 0, display: "flex", flexDirection: "column", maxHeight: "80vh" }}>
          <Box width="100%" textAlign="center" display="flex" flexDirection="column" flex={1} minHeight={0}>
            <Stack alignItems="center" flex={1} minHeight={0}>
              <Box height={ smUp ? "192px" : "120px"}  display="flex" justifyContent="center" alignItems="center" flexShrink={0}>
                  <img width="75px" height="75px" src={iconSrc} alt="icon" />
              </Box>
              {
                smUp ?  <Divider variant="middle" sx={{background: isMSPWarning ? theme.palette.error.main : theme.palette.primary.main, width: "85%", flexShrink: 0}}/> : null
              }
              <Box
              onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
              display="flex" flexDirection="column" flex={1} minHeight={0} width="100%">
                <Typography mx={smUp ? 10 : 1} my={smUp ? 2 : 1} variant={smUp ? "h6": "h7"} color={activeColor} textAlign="center" sx={{ overflowY: "auto" }}>
                  {t(`translation:${warnings[activeStep].message_ttoken}`)}
                </Typography>
                <Box flexShrink={0}>
                  <Box display="flex" justifyContent="center" alignItems="flex-end" visibility={warnings.length === 1 ? "hidden" : "unset"} paddingBottom={smUp ? "27px" : "17px"}>
                    <ClickableMobileStepper
                      activeStep={activeStep}
                      onStepClick={handleStepChange}
                      steps={warnings.length}
                      activeColor={activeColor}
                    />
                  </Box>
                  <Box display="flex" justifyContent={smUp ? "flex-end" : "center"}>
                    <Box width="175px" mb="15px">
                      <Button
                        fullWidth
                        color={activeColor}
                        size="medium"
                        onClick={handleClose}
                        variant="contained"
                        aria-label="close"
                        aria-controls="close"
                      >
                        {t("buttons.close")}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Stack>
          </Box>
        </DialogContent>
      </div>
    </DynamicDialog>
  );
};

export default PublicWarningDialog;
